/**
 * Default/fallback column templates for tables
 * Used when column config is not available from the API
 */

export const TABLE_COLUMN_TEMPLATES = {
    UserCompanies: [
        {
            key: 'companyName',
            label: 'Company Name',
            renderType: 'text',
            renderConfig: { fieldPath: 'companyName', variant: 'body1', translateValue: true, sx: { fontWeight: 'medium' } }
        },
        {
            key: 'actions',
            label: 'Actions',
            renderType: 'actions',
            renderConfig: { buttons: [{ id: 'select', text: 'Select', variant: 'contained', actionKey: 'onSelect' }] }
        },
    ],
    // Mirrors tbl_table_column_configs (ManageUsers): live members of the selected company (authz).
    // details.<column> = a client-specific column of tbl_user_details (the person); companyDetails.<column>
    // = one of tbl_user_company_details (the person in this company). They differ per client: change them
    // here and in tbl_table_column_configs, never in code.
    // Own row: keycloakUserId equals the logged-in user's id -> no role change / removal.
    ManageUsers: [
        {
            key: 'fullName',
            label: 'Name',
            renderType: 'text',
            renderConfig: { fieldPath: 'fullName', fallbackFieldPaths: ['username'], variant: 'body1', translateValue: false, sx: { fontWeight: 'medium' } }
        },
        {
            key: 'email',
            label: 'Email',
            renderType: 'text',
            renderConfig: { fieldPath: 'email', variant: 'body1', translateValue: false }
        },
        {
            key: 'mobileNumber',
            label: 'Mobile',
            renderType: 'text',
            renderConfig: { fieldPath: 'companyDetails.mobile_number', variant: 'body1', translateValue: false }
        },
        {
            key: 'idType',
            label: 'ID Type',
            renderType: 'text',
            renderConfig: { fieldPath: 'details.id_type', variant: 'body1', translateValue: true }
        },
        {
            key: 'idNumber',
            label: 'ID Number',
            renderType: 'text',
            renderConfig: { fieldPath: 'details.id_number', variant: 'body1', translateValue: false }
        },
        {
            key: 'roles',
            label: 'Roles',
            renderType: 'chips',
            renderConfig: { fieldPath: 'roles', labelPath: 'displayName', fallbackLabelPath: 'roleCode', translateValue: true }
        },
        {
            key: 'memberSince',
            label: 'Member since',
            renderType: 'date',
            renderConfig: { fieldPath: 'memberSince', variant: 'body1', format: 'dd/MM/yyyy' }
        },
        {
            key: 'actions',
            label: 'Actions',
            renderType: 'actions',
            renderConfig: {
                buttons: [
                    { id: 'modifyRoles', icon: 'AdminPanelSettings', tooltip: 'Modify User Roles', actionKey: 'onModifyRoles', hideCondition: { field: 'keycloakUserId', operator: 'equalsContext', contextField: 'currentUserId' } },
                    { id: 'delete', icon: 'Delete', tooltip: 'Remove', color: 'error', actionKey: 'onDelete', hideCondition: { field: 'keycloakUserId', operator: 'equalsContext', contextField: 'currentUserId' } },
                    { id: 'edit', icon: 'Edit', tooltip: 'Edit', color: 'secondary', actionKey: 'onEdit' }
                ]
            }
        },
    ],
    ManageTrucks: [
        {
            key: 'plateNumber',
            label: 'Plate Number',
            renderType: 'text',
            renderConfig: { fieldPath: 'plateNumber', variant: 'body1', translateValue: true, sx: { fontWeight: 'medium' } }
        },
        {
            key: 'type',
            label: 'Vehicle Type',
            renderType: 'text',
            renderConfig: { fieldPath: 'type', variant: 'body1', translateValue: true }
        },
        {
            key: 'subType',
            label: 'Vehicle Subtype',
            renderType: 'text',
            renderConfig: { fieldPath: 'subType', variant: 'body1', translateValue: true }
        },
        {
            key: 'size',
            label: 'Vehicle Gross Weight',
            renderType: 'text',
            renderConfig: { fieldPath: 'size', variant: 'body1', translateValue: true }
        },
        {
            key: 'tareWeight',
            label: 'Vehicle Tare Weight',
            renderType: 'text',
            renderConfig: { fieldPath: 'tareWeight', variant: 'body1', translateValue: true }
        },
        {
            key: 'capacity',
            label: 'Vehicle Load Capacity',
            renderType: 'text',
            renderConfig: { fieldPath: 'capacity', variant: 'body1', translateValue: true }
        },
        {
            key: 'capacityUnit',
            label: 'Vehicle Load Capacity UOM',
            renderType: 'text',
            renderConfig: { fieldPath: 'capacityUnit', variant: 'body1', translateValue: true }
        },
        {
            key: 'vehicleSerialNumber',
            label: 'Vehicle Serial Number',
            renderType: 'text',
            renderConfig: { fieldPath: 'vehicleSerialNumber', variant: 'body1', translateValue: true }
        },
        {
            key: 'actions',
            label: 'Actions',
            renderType: 'actions',
            renderConfig: {
                buttons: [
                    { id: 'view', icon: 'Visibility', tooltip: 'View', actionKey: 'onView' },
                    { id: 'edit', icon: 'Edit', tooltip: 'Edit', color: 'secondary', actionKey: 'onEdit' },
                    { id: 'delete', icon: 'Delete', tooltip: 'Remove', color: 'error', actionKey: 'onDelete' }
                ]
            }
        },
    ],
    ManageBranches: [
        {
            key: 'displayName',
            label: 'Branch Name',
            renderType: 'text',
            renderConfig: { fieldPath: 'displayName', variant: 'body1', translateValue: false, sx: { fontWeight: 'medium' } }
        },
        {
            key: 'municipality',
            label: 'Municipality',
            renderType: 'text',
            renderConfig: { fieldPath: 'municipality.label', variant: 'body1', translateValue: false }
        },
        {
            key: 'address',
            label: 'Address',
            renderType: 'text',
            renderConfig: { fieldPath: 'address', variant: 'body1', translateValue: false }
        },
        {
            key: 'actions',
            label: 'Action',
            renderType: 'actions',
            renderConfig: {
                buttons: [
                    { id: 'delete', icon: 'Delete', tooltip: 'Delete', color: 'error', actionKey: 'onDelete' }
                ]
            }
        },
    ],
    ManageGenerators: [
        {
            key: 'companyName',
            label: 'Facility Name',
            renderType: 'text',
            renderConfig: { fieldPath: 'companyName', variant: 'body1', translateValue: true, sx: { fontWeight: 'medium' } }
        },
        {
            key: 'generatedWasteType',
            label: 'Generated Waste Type',
            renderType: 'text',
            renderConfig: { fieldPath: 'generatedWasteType', variant: 'body1', translateValue: true }
        },
        {
            key: 'wasteType',
            label: 'Company Type',
            renderType: 'text',
            renderConfig: { fieldPath: 'wasteType', variant: 'body1', translateValue: true }
        },
        {
            key: 'unn',
            label: 'Unified National Number',
            renderType: 'text',
            renderConfig: { fieldPath: 'unn', variant: 'body1', translateValue: true }
        },
        {
            key: 'crNumber',
            label: 'CR Number',
            renderType: 'text',
            renderConfig: { fieldPath: 'crNumber', variant: 'body1', translateValue: true }
        },
        {
            key: 'phoneNumber',
            label: 'Phone Number',
            renderType: 'text',
            renderConfig: { fieldPath: 'phoneNumber', variant: 'body1', translateValue: true }
        },
        {
            key: 'actions',
            label: 'Actions',
            renderType: 'actions',
            renderConfig: {
                buttons: [
                    { id: 'view', icon: 'Visibility', tooltip: 'View', actionKey: 'onView' },
                    { id: 'edit', icon: 'Edit', tooltip: 'Edit', color: 'secondary', actionKey: 'onEdit' },
                    { id: 'delegates', icon: 'Group', tooltip: 'Delegates', actionKey: 'onDelegates' },
                    { id: 'delete', icon: 'Delete', tooltip: 'Remove', color: 'error', actionKey: 'onDelete' }
                ]
            }
        },
    ],
    ManageContacts: [
        {
            key: 'id',
            label: 'ID',
            renderType: 'text',
            renderConfig: { fieldPath: 'id', variant: 'body1', translateValue: true }
        },
        {
            key: 'firstName',
            label: 'First Name',
            renderType: 'text',
            renderConfig: { fieldPath: 'firstName', variant: 'body1', translateValue: true, sx: { fontWeight: 'medium' } }
        },
        {
            key: 'lastName',
            label: 'Last Name',
            renderType: 'text',
            renderConfig: { fieldPath: 'lastName', variant: 'body1', translateValue: true }
        },
        {
            key: 'nationalId',
            label: 'National Id',
            renderType: 'text',
            renderConfig: { fieldPath: 'nationalId', variant: 'body1', translateValue: true }
        },
        {
            key: 'mobileNumber',
            label: 'Mobile Number',
            renderType: 'text',
            renderConfig: { fieldPath: 'mobileNumber', variant: 'body1', translateValue: true }
        },
        {
            key: 'address',
            label: 'Address',
            renderType: 'text',
            renderConfig: { fieldPath: 'address', variant: 'body1', translateValue: true }
        },
        {
            key: 'email',
            label: 'Email',
            renderType: 'text',
            renderConfig: { fieldPath: 'email', variant: 'body1', translateValue: true }
        },
        {
            key: 'generatorCompanyId',
            label: 'Company',
            renderType: 'text',
            renderConfig: { fieldPath: 'companyName', fallbackFieldPaths: ['generatorCompanyName', 'generatorCompany', 'company', 'generatorCompanyId'], variant: 'body1', translateValue: true }
        },
        {
            key: 'actions',
            label: 'Actions',
            renderType: 'actions',
            renderConfig: {
                buttons: [
                    { id: 'view', icon: 'Visibility', tooltip: 'View', actionKey: 'onView' },
                    { id: 'edit', icon: 'Edit', tooltip: 'Edit', color: 'secondary', actionKey: 'onEdit' },
                    { id: 'delete', icon: 'Delete', tooltip: 'Remove', color: 'error', actionKey: 'onDelete' }
                ]
            }
        },
    ],
    ManageCompanyContacts: [
        {
            key: 'fullName',
            label: 'Full Name',
            renderType: 'text',
            renderConfig: { fieldPath: 'fullName', variant: 'body1', translateValue: true, sx: { fontWeight: 'medium' } }
        },
        {
            key: 'email',
            label: 'Email',
            renderType: 'text',
            renderConfig: { fieldPath: 'email', variant: 'body1', translateValue: true }
        },
        {
            key: 'mobileNumber',
            label: 'Mobile Number',
            renderType: 'text',
            renderConfig: { fieldPath: 'mobileNumber', variant: 'body1', translateValue: true }
        },
        {
            key: 'idType',
            label: 'ID Type',
            renderType: 'text',
            renderConfig: { fieldPath: 'idType', variant: 'body1', translateValue: true }
        },
        {
            key: 'idNumber',
            label: 'ID Number',
            renderType: 'text',
            renderConfig: { fieldPath: 'idNumber', variant: 'body1', translateValue: true }
        },
        {
            key: 'createdDate',
            label: 'Creation Date',
            sortable: true,
            renderType: 'text',
            renderConfig: { fieldPath: 'createdDate', variant: 'body1', translateValue: true }
        },
        {
            key: 'actions',
            label: 'Actions',
            renderType: 'actions',
            renderConfig: {
                buttons: [
                    { id: 'delete', icon: 'Delete', tooltip: 'Remove', color: 'error', actionKey: 'onDelete' },
                    { id: 'edit', icon: 'Edit', tooltip: 'Edit', color: 'secondary', actionKey: 'onEdit' }
                ]
            }
        },
    ],
    // Mirrors tbl_table_column_configs (ManageTemplates): one row per template version.
    ManageTemplates: [
        {
            key: 'templateName',
            label: 'Name',
            renderType: 'text',
            renderConfig: { fieldPath: 'templateName', variant: 'body1', translateValue: false, sx: { fontWeight: 'medium' } }
        },
        {
            key: 'version',
            label: 'Version',
            renderType: 'conditional',
            renderConfig: {
                condition: { field: 'isInUse', operator: 'equals', value: true },
                trueRender: { renderType: 'chip', renderConfig: { fieldPath: 'versionLabel', color: 'success', translateValue: false } },
                falseRender: { renderType: 'text', renderConfig: { fieldPath: 'versionLabel', variant: 'body1', translateValue: false } }
            }
        },
        {
            key: 'extension',
            label: 'Extension',
            renderType: 'text',
            renderConfig: { fieldPath: 'extension', variant: 'body1', translateValue: false }
        },
        {
            key: 'createdDate',
            label: 'Uploaded on',
            renderType: 'date',
            renderConfig: { fieldPath: 'createdDate', variant: 'body1', format: 'dd/MM/yyyy HH:mm' }
        },
        {
            key: 'actions',
            label: 'Actions',
            renderType: 'actions',
            renderConfig: {
                buttons: [
                    { id: 'download', icon: 'Download', tooltip: 'Download', actionKey: 'onDownload' },
                    { id: 'use', icon: 'Restore', tooltip: 'Use this version', actionKey: 'onUse', condition: { field: 'isInUse', operator: 'equals', value: false } },
                    { id: 'delete', icon: 'Delete', tooltip: 'Delete', color: 'error', actionKey: 'onDelete' }
                ]
            }
        },
    ],
    // Mirrors tbl_table_column_configs (WorkflowHistory). Only used when that fetch fails or the
    // domain has no rows; field names are the "My processes" row fields (mapMyProcessResponse).
    WorkflowHistory: [
        {
            key: 'generated_id',
            label: 'Process ID',
            renderType: 'text',
            renderConfig: { fieldPath: 'generated_id', variant: 'body1', translateValue: false, sx: { fontWeight: 'medium' } }
        },
        {
            key: 'task_name',
            label: 'Task Name',
            renderType: 'text',
            renderConfig: { fieldPath: 'task_name', variant: 'body1', translateValue: true }
        },
        {
            key: 'userTaskAssignee',
            label: 'Task Assignee',
            renderType: 'text',
            renderConfig: { fieldPath: 'current_assignee', variant: 'body1', translateValue: false }
        },
        {
            key: 'modified_at',
            label: 'Modification Date',
            sortable: true,
            renderType: 'date',
            renderConfig: { fieldPath: 'modified_at', variant: 'body1', translateValue: false }
        },
        {
            key: 'state',
            label: 'Status',
            sortable: true,
            renderType: 'text',
            renderConfig: { fieldPath: 'state', variant: 'body1', translateValue: true }
        },
        {
            key: 'actions',
            label: 'Actions',
            renderType: 'actions',
            renderConfig: {
                buttons: [
                    { id: 'view', icon: 'Visibility', tooltip: 'View', actionKey: 'onView' },
                    { id: 'delete', icon: 'Delete', tooltip: 'Remove', color: 'error', actionKey: 'onDelete', condition: { operator: 'and', conditions: [{ field: 'process_definition_key', operator: 'hasWorkflowPermission', permission: 'delete_workflow' }, { field: 'can_delete', operator: 'equals', value: true }] } },
                    { id: 'download', icon: 'Download', tooltip: 'Download PDF', actionKey: 'onDownload', condition: { operator: 'and', conditions: [{ field: 'process_definition_key', operator: 'hasWorkflowPermission', permission: 'download_pdf' }, { field: 'has_ready_documents', operator: 'equals', value: true }] } },
                    { id: 'reassign', icon: 'SwapHoriz', tooltip: 'Reassign', color: 'secondary', actionKey: 'onReassign', condition: { operator: 'and', conditions: [{ field: 'process_definition_key', operator: 'hasWorkflowPermission', permission: 'reassign_task' }, { field: 'can_reassign', operator: 'equals', value: true }] } },
                    { id: 'regenerate', icon: 'ChangeCircle', tooltip: 'Regenerate', actionKey: 'onRegenerate', condition: { operator: 'and', conditions: [{ field: 'process_definition_key', operator: 'hasWorkflowPermission', permission: 'regenerate_pdf' }, { field: 'has_documents', operator: 'equals', value: true }] } },
                    { id: 'clone', icon: 'ContentCopy', tooltip: 'Clone', actionKey: 'onClone', condition: { field: 'process_definition_key', operator: 'hasWorkflowPermission', permission: 'clone_workflow' } },
                ]
            }
        },
    ],
    // Mirrors tbl_table_column_configs (UnassignedTasks). Only used when that fetch fails, so the
    // Claim button carries the same permission condition: the fallback never shows more than the
    // config. Same row shape as PendingTasks; no Assignee column (always empty here).
    UnassignedTasks: [
        {
            key: 'process_instance_id',
            label: 'Process ID',
            renderType: 'text',
            renderConfig: { fieldPath: 'process_instance_id', variant: 'body1', translateValue: false, sx: { fontWeight: 'medium' } }
        },
        {
            key: 'process_definition_key',
            label: 'Process Name',
            sortable: true,
            sortByLabel: true,
            renderType: 'text',
            renderConfig: { fieldPath: 'process_definition_key', variant: 'body1', translateValue: true }
        },
        {
            key: 'task_name',
            label: 'User Task Name',
            sortable: true,
            sortByLabel: true,
            renderType: 'text',
            renderConfig: { fieldPath: 'task_name', variant: 'body1', translateValue: true }
        },
        {
            key: 'started_at',
            label: 'Creation Date',
            sortable: true,
            renderType: 'date',
            renderConfig: { fieldPath: 'started_at', variant: 'body1', translateValue: false }
        },
        {
            key: 'actions',
            label: 'Actions',
            renderType: 'actions',
            renderConfig: {
                buttons: [
                    { id: 'claim', icon: 'PlayArrow', tooltip: 'Claim Task', actionKey: 'onClaim', condition: { field: 'process_definition_key', operator: 'hasWorkflowPermission', permission: 'claim_task' } },
                ]
            }
        },
    ],
    // Mirrors tbl_table_column_configs (WorkflowList). Only used when that fetch fails, so the
    // buttons carry the same permission conditions: the fallback never shows more than the config.
    WorkflowList: [
        {
            key: 'name',
            label: 'Workflow Name',
            renderType: 'text',
            renderConfig: { fieldPath: 'displayName', variant: 'body1', translateValue: true, sx: { fontWeight: 'medium' } }
        },
        {
            key: 'actions',
            label: 'Actions',
            renderType: 'actions',
            renderConfig: {
                buttons: [
                    { id: 'start', icon: 'PlayArrow', label: 'Start', tooltip: 'Start Process', actionKey: 'onStart', condition: { field: 'processDefinitionKey', operator: 'hasWorkflowPermission', permission: 'start_workflow' } },
                    { id: 'clone', icon: 'ContentCopy', label: 'Clone', tooltip: 'lastclone', actionKey: 'onClone', condition: { field: 'processDefinitionKey', operator: 'hasWorkflowPermission', permission: 'clone_workflow' } },
                ]
            }
        },
    ],
    // Mirrors tbl_table_column_configs (PendingTasks). Only used when that fetch fails, so the
    // buttons carry the same permission conditions: the fallback never shows more than the config.
    PendingTasks: [
        {
            key: 'process_instance_id',
            label: 'Process ID',
            renderType: 'text',
            renderConfig: { fieldPath: 'process_instance_id', variant: 'body1', translateValue: false, sx: { fontWeight: 'medium' } }
        },
        {
            key: 'process_definition_key',
            label: 'Process Name',
            sortable: true,
            sortByLabel: true,
            renderType: 'text',
            renderConfig: { fieldPath: 'process_definition_key', variant: 'body1', translateValue: true }
        },
        {
            key: 'task_name',
            label: 'User Task Name',
            sortable: true,
            sortByLabel: true,
            renderType: 'text',
            renderConfig: { fieldPath: 'task_name', variant: 'body1', translateValue: true }
        },
        {
            key: 'assignee',
            label: 'Assignee',
            renderType: 'text',
            renderConfig: { fieldPath: 'assignee', variant: 'body1', translateValue: false }
        },
        {
            key: 'started_at',
            label: 'Creation Date',
            sortable: true,
            renderType: 'date',
            renderConfig: { fieldPath: 'started_at', variant: 'body1', translateValue: false }
        },
        {
            key: 'actions',
            label: 'Actions',
            renderType: 'actions',
            renderConfig: {
                buttons: [
                    { id: 'execute', icon: 'PlayArrow', tooltip: 'execute_task', actionKey: 'onExecute', condition: { field: 'process_definition_key', operator: 'hasWorkflowPermission', permission: 'execute_workflow' } },
                    { id: 'downloadPdf', icon: 'Download', tooltip: 'Download PDF', actionKey: 'onDownloadPdf', condition: { operator: 'and', conditions: [{ field: 'process_definition_key', operator: 'hasWorkflowPermission', permission: 'download_pdf' }, { field: 'has_ready_documents', operator: 'equals', value: true }] } },
                    { id: 'delete', icon: 'Delete', color: 'error', tooltip: 'Delete', actionKey: 'onDelete', condition: { operator: 'and', conditions: [{ field: 'process_definition_key', operator: 'hasWorkflowPermission', permission: 'delete_workflow' }, { field: 'can_delete', operator: 'equals', value: true }] } },
                    { id: 'unclaim', icon: 'LockOpen', tooltip: 'Unclaim Task', actionKey: 'onUnclaim', condition: { field: 'process_definition_key', operator: 'hasWorkflowPermission', permission: 'claim_task' } },
                ]
            }
        },
    ],
    // Add more table templates below...
};
